import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Flowlib } from '@flowlib/ui';
import '@flowlib/ui/styles';
import { auth } from '@flowlib/user-auth';
import { authFrontend } from '@flowlib/user-auth/ui';
import { rbac } from '@flowlib/rbac';
import { webhooks } from '@flowlib/webhooks';
import { versionControl } from '@flowlib/version-control';
import { mcp } from '@flowlib/mcp';
// @flowlib/agents — browser entry exports both the listing/CRUD plugin
// (Stream L) and the chat-surface plugin (Stream M). Compose both so
// /agents and /agents/:agentId/sessions/:sessionId both render.
import { agents, agentsChatFrontendPlugin } from '@flowlib/agents';
import { CreditCard, Users } from 'lucide-react';
import { HostedAuthShell } from './auth/HostedAuthShell';
import { MembersPage } from './auth/MembersPage';
import { BillingPage } from './billing/BillingPage';

// All five plugin imports resolve via the package `browser` export condition,
// which returns frontend-only definitions. The backend (apps/api) implements
// the routes each frontend calls — see apps/api/src/routes/plugins/*.ts.
const apiPath = import.meta.env.VITE_API_PATH ?? '/api';

// Override the upstream auth shell with our hosted variant. HostedAuthShell
// is a thin wrapper around upstream `<AuthAppShell>` that:
//   - Configures sign-up to use magic-link only (no password capture).
//   - Enables Google + GitHub social providers.
//   - Adds <OnboardingGate> for org/workspace capture (multi-tenant SaaS).
//
// Also replaces the `/users` route + sidebar item: the upstream
// UserManagementPage manages BA's *global* `user.role` which is a single-
// tenant concept and is meaningless in this multi-tenant SaaS. Our hosted
// MembersPage manages members of the caller's *active organization*,
// gated by Better Auth's org plugin (owner/admin can manage; member sees
// a "contact an admin" notice).
const hostedAuthFrontend = {
  ...authFrontend,
  appShell: HostedAuthShell,
  sidebar: [
    ...(authFrontend.sidebar ?? []).filter((s) => s.path !== '/users'),
    {
      label: 'Members',
      icon: Users,
      path: '/members',
      position: 'top' as const,
    },
    {
      label: 'Billing',
      icon: CreditCard,
      path: '/settings/billing',
      position: 'bottom' as const,
    },
  ],
  routes: [
    ...(authFrontend.routes ?? []).filter((r) => r.path !== '/users'),
    { path: '/members', component: MembersPage },
    { path: '/settings/billing', component: BillingPage },
  ],
};

const hostedAuth = {
  ...auth(),
  frontend: hostedAuthFrontend,
};

const root = document.getElementById('root');
if (!root) {
  throw new Error('#root not found');
}

createRoot(root).render(
  <StrictMode>
    <Flowlib
      config={{
        apiPath,
        frontendPath: '/',
        theme: 'dark',
        plugins: [
          hostedAuth,
          rbac(),
          webhooks(),
          versionControl(),
          mcp(),
          // Listing/CRUD pages — sidebar entry, /agents/*, hooks, store.
          agents(),
          // Chat surface — separate plugin so the chat route + WebSocket
          // wiring can be omitted by hosts that only want the CRUD side.
          { id: 'agents-chat', name: 'Agents Chat', frontend: agentsChatFrontendPlugin },
        ],
      }}
    />
  </StrictMode>,
);
